<template lang="html">
  <div class="multi__select__wrp" ref="wrapper">
    <div
      class="multi__select"
      :class="{
        has__value: selectedItems.length,
        in__focus: inFocus
      }"
      @click="onSelectClicked">
      <div ref="select"></div>
      <div class="placeholder" v-show="!inFocus && !selectedItems.length">{{placeholder}}</div>
      <div class="arrow__icon__wrp" @click.prevent="arrowClicked">
        <div
          class="arrow__icon"
          :class="{ up__icon: inFocus }"
          v-html="require('@/assets/img/input-droplist.svg')"
        ></div>
      </div>
      <div class="selected__items">
        <div class="selected__item" v-for="item in selectedItems" :key="item.value">
          <div class="selected__label">{{item.label}}</div>
          <div
            class="remove__icon"
            @click="removeClicked(item.value)"
            v-html="require('@/assets/img/remove-item.svg')">
          </div>
        </div>
        <input
          class="input__el"
          ref="input"
          v-model="filterText"
          @blur="onBlurInput"
          :style="inputWidth"
        />
      </div>
    </div>
    <div class="options__wrp">
      <ul
        class="options__items"
        v-show="inFocus && (filteredOptions.length || !filteredOptions.length && filterText)"
        ref="options__items"
        :style="{
          top: listStyle.top,
          left: listStyle.left,
        }"
      >
        <li
          class="option__item"
          v-for="option in filteredOptions"
          :key="option.value"
          @click.stop="optionClicked(option.value)">
          {{option.label}}
        </li>
        <li
          class="empty__item"
          v-show="!filteredOptions.length && filterText">
          Ничего не найдено
        </li>
      </ul>
    </div>

    <slot />
  </div>
</template>

<script>
  export default {
    name: 'bimMultiSelect',
    props: {
      /**
       * Значение селекта (берется из v-model)
       */
      value: {
        type: Array,
        default: () => ([])
      },
      /**
       * Текст селекта
       */
      placeholder: {
        type: String,
        default: ''
      },
      /**
       * Массив options [{ label:'один', value: 1 }, { label:'два', value: 2 }, {}]
       */
      options: {
        type: Array,
        prop: {
          label: String,
          value: [String, Number]
        },
        default: () => ([])
      }
    },
    data () {
      return {
        inFocus: false,
        observableValue: this.value,
        filterText: '',
        blurTimeout: null,
        listStyle: {
          top: 0,
          left: 0,
        }
      }
    },
    mounted () {
      document.body.appendChild(this.$refs.options__items)
      this.$refs.options__items.style.width = window.getComputedStyle(this.$refs.wrapper).width
    },
    beforeDestroy () {
      document.body.removeChild(this.$refs.options__items)
    },
    computed: {
      inputWidth () {
        let maxWidth = 300
        const select = this.$refs.select

        if (select) {
          maxWidth = select.offsetWidth
        }

        let width = String(this.filterText).length * 9 + 20

        if (width >= maxWidth) {
          width = maxWidth - 5
        }

        return `width: ${width}px`
      },
      selectedItems () {
        const items = []

        this.observableValue.forEach(value => {
          const option = this.options.find(options => options.value === value)

          if (option) {
            items.push(option)
          }
        })

        return items
      },
      filteredOptions () {
        const options = []

        this.options.forEach(option => {
          if (!this.observableValue.find(val => val === option.value)) {
            options.push(option)
          }
        })

        if (!this.filterText) {
          return options
        }

        const filter = String(this.filterText).toLowerCase()

        return options.filter(option => {
          const label = String(option.label).toLowerCase()

          return ~label.indexOf(filter)
        })
      }
    },
    methods: {
      onSelectClicked () {
        this.inFocus = true
        this.$refs.input.focus()
        clearTimeout(this.blurTimeout)
        this.setDocumentScrollListener()
        this.setListOffset()
      },
      setDocumentScrollListener () {
        document.addEventListener('scroll', this.documentScrollListener)
      },
      removeDocumentScrollListener () {
        document.removeEventListener('scroll', this.documentScrollListener)
      },
      documentScrollListener () {
        window.requestAnimationFrame(this.setListOffset)
      },
      setListOffset () {
        const wrapper = this.$refs.wrapper
        const boundedWrapper = wrapper.getBoundingClientRect()
        this.listStyle.left = boundedWrapper.left + 'px'
        this.listStyle.top = boundedWrapper.top + boundedWrapper.height + 15 + 'px'
      },
      removeClicked (value) {
        const index = this.observableValue.findIndex(oValue => oValue === value)

        if (this.inFocus) {
          clearTimeout(this.blurTimeout)
          this.onSelectClicked()
        }

        if (~index) {
          this.observableValue.splice(index, 1)

          /**
           * Событие срабатывает при выборе или удалении
           * @property value Array
           */
          this.$emit('input', this.observableValue)
        }
      },
      onBlurInput () {
        this.blurTimeout = setTimeout(() => {
          this.$emit('blur')
          this.inFocus = false
          this.filterText = ''
          this.removeDocumentScrollListener()
        }, 350)
      },
      optionClicked (value) {
        clearTimeout(this.blurTimeout)
        this.onSelectClicked()
        this.filterText = ''

        if (!this.observableValue.find(val => val === value)) {
          this.observableValue.push(value)
          this.$emit('input', this.observableValue)
        }
      },
      arrowClicked (e) {
        if (this.inFocus) {
          this.onBlurInput()
          this.$refs.input.blur()
        } else {
          this.onSelectClicked()
        }
      }
    },
    watch: {
      value (value) {
        this.observableValue = value
      }
    }
  }
</script>

<style lang="scss" scoped>
  $height: 36px;
  $border-weight: 1px;

  .multi__select__wrp {
    position: relative
  }

  .multi__select {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    min-height: $height;
    border: $border-weight solid $white-color-700;
    border-radius: 5px;
    padding: 5px 30px 5px 5px;
    outline: none;
    cursor: text;
    transition: border-color 0.2s;
  }

  .multi__select.has__value {
    padding: 5px 27px 0 5px;
  }

  .multi__select:hover {
    border-color: $black-color-50;
  }

  .options__wrp {
    position: relative;
  }

  .placeholder {
    position: absolute;
    z-index: 3;
    left: 15px;
    top: 0;
    right: 4px;
    pointer-events: none;
    line-height: $height;
    font-size: 15px;
    color: rgba(67, 67, 67, .4);
    transition: transform 0.3s ease, color 0.2s;
    transform-origin: 0 0%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .input__el {
    width: 40px;
    font-family: inherit;
    font-size: 15px;
    outline: none;
    background-color: transparent;
    border: none;
    line-height: 22px;
    margin-left: 10px;
    float: left;
  }

  .options__items {
    margin: 0;
    list-style-type: none;
    position: fixed;
    background-color: white;
    z-index: 1011;
    border-radius: 5px;
    padding: 5px 0;
    max-height: 200px;
    overflow-y: auto;
    box-shadow: 0 10px 30px rgba(0,0,0, .05);
    color: $black-color-300;
  }

  .option__item,
  .empty__item {
    padding: 10px 14px;
    font-size: 15px;
    cursor: pointer;
    letter-spacing: 0.2px;
  }

  .empty__item {
    cursor: default;
    color: #888;
  }

  .option__item:hover {
    background-color: rgba(0,0,0, .05);
  }

  .selected__item {
    background-color: $primary-color-50 ;
    padding: 5px 28px 5px 8px;
    border-radius: 4px;
    float: left;
    position: relative;
    max-width: 100%;
    margin-bottom: 5px;
    margin-right: 5px;
    font-size: 15px;
  }

  .selected__items:after {
    content: '';
    display: block;
    clear: both;
  }

  .remove__icon {
    position: absolute;
    top: 50%;
    right: 5px;
    transform: translateY(-55%);
    opacity: 0.5;
    cursor: pointer;
  }

  .remove__icon:hover {
    opacity: 0.9;
  }

  .remove__icon /deep/ svg {
    width: 10px;
    display: block;
  }

  .selected__label {
    word-wrap: break-word;
  }

  .arrow__icon {
    position: absolute;
    top: 50%;
    right: 14px;
    display: block;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .arrow__icon /deep/ svg {
    display: block;
  }

  .arrow__icon.up__icon {
    transform: translateY(-50%) rotate(180deg);
  }

  .arrow__icon__wrp {
    width: 35px;
    height: $height - ($border-weight * 2);
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    user-select: none;
  }
</style>
